<template>
    <div class="table-container">
        <div class="table__controls">
            <div class="table__size-selector">
                <select id="pagesize" v-model="pageSize" @change="currentPage = 1">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
                <div>{{ labels.Entries }}</div>
            </div>
            <div class="table__search">

                <label for="search" class="table__search-input-label">{{ labels.SearchText }}</label>
                <input id="search" type="text" v-model="searchTerm" :placeholder="labels.SearchPlaceholder"
                    class="table__search-input" />
            </div>
        </div>

        <table v-if="paginatedRows.length > 0" class="vue-data-table">
            <thead>
                <tr>
                    <th v-for="key in columns" :key="key" @click="sortBy(key)" class="table__sortable">
                        {{ key }}
                        <span v-if="sortKey === key" class="sort-icon">
                            {{ sortOrders[key] > 0 ? '▲' : '▼' }}
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in paginatedRows" :key="index">
                    <td v-for="key in columns" :key="key">
                        {{ row[key] }}
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-else-if="isLoading" class="loader">{{ labels.Loading }}</div>
        <div v-else class="no-data">{{ labels.NoData }}</div>

        <div v-if="totalPages > 1" class="table__pagination">
            <div class="table__pagination-summary">
                {{ labels.PaginationText }} {{ paginatedRows.length }}
                {{ labels.PaginationTextMiddle }} {{ sortedRows.length }}
                {{ labels.PaginationTextLast }}
            </div>

            <div class="table__pagination-nav">
                <button :disabled="currentPage === 1" @click="currentPage--" class="table__pagination-nav-arrow">
                    &lsaquo;
                </button>

                <template v-for="(page, index) in displayedPages" :key="index">
                    <span v-if="page === '...'" class="table__pagination-ellipsis">...</span>
                    <button v-else @click="currentPage = page"
                        :class="['table__page-number', { active: currentPage === page }]">
                        {{ page }}
                    </button>
                </template>

                <button :disabled="currentPage === totalPages" @click="currentPage++"
                    class="table__pagination-nav-arrow">
                    &rsaquo;
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: "Table",
    data() {
        return {
            rows: [],
            searchTerm: '',
            labels: {},
            sortKey: '',
            sortOrders: {},
            isLoading: false,
            currentPage: 1,
            pageSize: 10,
            serviceUrl: "/umbraco/api/affiliatesApi/getAffiliates"
        };
    },
    computed: {
        columns() {
            return this.rows.length > 0 ? Object.keys(this.rows[0]) : [];
        },
        // 1. First, filter by the search term
        filteredBySearch() {
            if (!this.searchTerm) return this.rows;

            const term = this.searchTerm.toLowerCase();
            return this.rows.filter(row => {
                return Object.values(row).some(val =>
                    String(val).toLowerCase().includes(term)
                );
            });
        },
        // 2. Second, sort the filtered data
        sortedRows() {
            let data = [...this.filteredBySearch];
            if (this.sortKey) {
                const order = this.sortOrders[this.sortKey] || 1;
                data.sort((a, b) => {
                    const valA = a[this.sortKey];
                    const valB = b[this.sortKey];
                    return (valA === valB ? 0 : valA > valB ? 1 : -1) * order;
                });
            }
            return data;
        },
        // 3. Third, slice for pagination
        paginatedRows() {
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            return this.sortedRows.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.sortedRows.length / this.pageSize);
        },
        displayedPages() {
            const total = this.totalPages;
            const current = this.currentPage;

            // If total pages are very small (e.g., less than 6), just show them all
            if (total <= 5) {
                return Array.from({ length: total }, (_, i) => i + 1);
            }

            const rangeWithDots = [];

            // 1. Always Add Page 1
            rangeWithDots.push(1);

            // 2. Add first ellipsis if current page is far from the start
            if (current > 3) {
                rangeWithDots.push('...');
            } else if (current === 3) {
                rangeWithDots.push(2);
            }

            // 3. Add the "Middle 3" (Current and its neighbors)
            let start = Math.max(2, current - 1);
            let end = Math.min(total - 1, current + 1);

            // Adjust window if we are at the very beginning
            if (current <= 3) {
                start = 2;
                end = 4;
            }
            // Adjust window if we are at the very end
            if (current >= total - 2) {
                start = total - 3;
                end = total - 1;
            }

            for (let i = start; i <= end; i++) {
                // Prevent duplicates if the window includes page 2 or total-1
                if (!rangeWithDots.includes(i)) {
                    rangeWithDots.push(i);
                }
            }

            // 4. Add second ellipsis if current page is far from the end
            if (current < total - 2) {
                rangeWithDots.push('...');
            } else if (current === total - 2) {
                if (!rangeWithDots.includes(total - 1)) {
                    rangeWithDots.push(total - 1);
                }
            }

            // 5. Always Add Last Page
            if (!rangeWithDots.includes(total)) {
                rangeWithDots.push(total);
            }

            return rangeWithDots;
        }
    },
    watch: {
        // Reset to page 1 if search changes
        searchTerm() {
            this.currentPage = 1;
        }
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.toggleSpinner(true);
            try {
                const response = await axios.get(this.serviceUrl);
                this.rows = response.data.rows;
                this.labels = response.data.labels;
                this.columns.forEach(key => {
                    this.sortOrders[key] = 1;
                });
            } catch (error) {
                console.error("Error fetching excel data", error);
            } finally {
                this.toggleSpinner(false);
            }
        },
        sortBy(key) {
            this.sortKey = key;
            this.sortOrders[key] = this.sortOrders[key] * -1;
        },
        toggleSpinner(isLoading) {
            this.isLoading = isLoading;
            document.body.classList.toggle("is-loading", isLoading);
        }
    }
};
</script>